import { Plan } from '~/auth';
import { GetProvidersParams, ProviderResultItem, recommendedProvidersClient } from '~/clients';
import { SearchFormData } from '~/components/provider-search';
import { IS_APP_BY_PROVIDER_TYPE } from '~/components/provider-search/select-options';
import createLogger from '~/logging';

import { NullableSearchResults, SearchResults, UseSearchFormResultsParams } from './types';

const log = createLogger(__filename);

const LATEST_SEARCH_CACHE_KEY = 'garnerLatestProvidersQuery';

function buildGetProvidersParams(formData: SearchFormData): Omit<GetProvidersParams, 'plan' | 'isDemo'> | null {
  if (formData.careGoal && !('specialty' in formData.careGoal)) {
    log.error({ context: formData }, 'specialty was unexpectedly empty');
    return null;
  }

  return {
    specialty: formData.careGoal?.specialty ?? undefined,
    name: formData.providerName ?? undefined,
    networkId: formData.network.value,
    location: formData.location.position,
    language: formData.language ?? undefined,
    gender: formData.gender !== '' && formData.gender !== null ? formData.gender : undefined,
    isApp: formData.providerType ? IS_APP_BY_PROVIDER_TYPE[formData.providerType] : undefined,
  };
}

function getQueryKey(formData: SearchFormData) {
  const params = buildGetProvidersParams(formData);
  return JSON.stringify({
    ...params,
    // Geocoding a zip code directly can result in a _slightly_ different lat + lng than geocoding a fullAddress
    // that represents the same location, so key off the location
    location: formData.location.label,
    // Include careGoalId so that if they tried to search for another care goal with the same specialty we still search
    careGoalId: formData.careGoal?.value,
  });
}

/**
 * Remove overallScore from non top providers.
 * Sort providers array so that all top providers are at the top of the list and the ranking algorithm is otherwise maintained.
 * Provider name searches are sorted in the r12n service so we do not want to sort them by isTopProvider.
 */
export function formatProviders(providers: ProviderResultItem[], sortTopProvidersFirst = true) {
  const formattedProviders = providers.map(({ overallScore, ...provider }) =>
    provider.isTopProvider ? { overallScore, ...provider } : provider,
  );

  if (sortTopProvidersFirst) {
    return formattedProviders.sort((a, b) => Number(b.isTopProvider) - Number(a.isTopProvider));
  }

  return formattedProviders;
}

export function useResultsCache() {
  return {
    getCacheValue(formData: SearchFormData): NullableSearchResults {
      const latestSearch = sessionStorage.getItem(LATEST_SEARCH_CACHE_KEY);
      if (!latestSearch) return null;

      const queryKey = getQueryKey(formData);
      const { queryKey: oldQueryKey, data } = JSON.parse(latestSearch);
      if (queryKey !== oldQueryKey) return null;

      return data;
    },
    setCacheValue(formData: SearchFormData, results: SearchResults) {
      const queryKey = getQueryKey(formData);
      sessionStorage.setItem(
        LATEST_SEARCH_CACHE_KEY,
        JSON.stringify({
          queryKey,
          data: results,
        }),
      );
    },
  };
}

export async function executeProviderSearch({
  formData,
  showAdditionalProviders,
  plan,
  isDemo,
  getCacheValue,
  setCacheValue,
}: UseSearchFormResultsParams & {
  plan: Plan;
  isDemo: boolean;
  getCacheValue: (formData: SearchFormData) => NullableSearchResults;
  setCacheValue: (formData: SearchFormData, results: SearchResults) => void;
}): Promise<SearchResults> {
  if (!formData) return { providers: [] };

  // Need explicit boolean check here, because `supportsProviderSearch` can be undefined for named doctor search
  if (formData.careGoal?.supportsProviderSearch === false) {
    return { providers: [], resultType: 'not_supported' };
  }

  const cachedValue = getCacheValue(formData);
  if (cachedValue) {
    log.debug({ context: formData }, 'Using providers from session storage');
    return cachedValue;
  }

  const params = buildGetProvidersParams(formData);
  if (!params) return { providers: [] };

  // NOTE: When the showAdditionalProviders feature flag is false, default to showing up to 30 top providers
  const additionalProvidersParams: Partial<GetProvidersParams> | undefined = showAdditionalProviders
    ? { includeLowQualityProviders: true, limit: 30, providerSearchSet: 'in_network' }
    : { limit: 30 };

  const response = await recommendedProvidersClient.getProviders({
    ...params,
    ...additionalProvidersParams,
    plan,
    isDemo,
  });

  // Deliberately not cached: a throttle carries no results and would outlive the rate-limit window,
  // so the message would persist after the limit had already reset.
  if (!response.success) return { providers: [], resultType: 'rate_limited' };

  const { providers: unsortedProviders, responseSetType: resultType } = response.data;
  const providers = formatProviders(unsortedProviders, !params.name);

  setCacheValue(formData, { providers, resultType });

  return { providers, resultType };
}
